import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DataTable } from "@/components/DataTable";
import usePagination from "@/hooks/pagination";
import { fetchAllUsers } from "@/api/allUsers";
import { getUserCompanies } from "@/api/user.service";
import {
  cicloAgir,
  cicloHistorico,
  type CicloComHistorico,
  cicloIniciar,
  cicloReabrir,
  listCenarios,
  promoverCenario,
  statusCalculo,
  type AcaoCiclo,
  type Cenario,
  type EtapaCiclo,
  type ParteCiclo,
} from "@/api/metas.service";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import { rotuloMes } from "./metasFormato";
import { corSituacao, situacaoDe, statusCalculoDe } from "./metasStatus";
import {
  ACAO_ROTULO,
  ETAPA_COR,
  ETAPA_ROTULO,
  MANIFESTACAO_ROTULO,
  botoesDaParte,
  escopoDaEtapa,
  progressoDoCiclo,
  type BotaoCiclo,
} from "./metasFluxo";
import { useFluxoCenario } from "./useFluxoCenario";

// Acompanhamento e execução do ciclo de aprovação das metas: cada responsável
// aprova o escopo de lojas e categorias dele, e a publicação só é liberada
// quando todos concluem. O servidor (metas.ciclo_*) é quem decide o que cada
// um pode fazer; a tela só oferece os botões que ele aceitaria. Ver os valores
// e ajustá-los continua na aba Manutenção de Metas.

const INTERVALO_PUBLICACAO_MS = 4_000;
const LIMITE_PUBLICACAO_MS = 30 * 60 * 1_000;

interface Pendente {
  parte: ParteCiclo;
  botao: BotaoCiclo;
}

const dataHora = (iso: string) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

export function MetasAprovacoesTab({ onAbrirManutencao }: { onAbrirManutencao?: () => void } = {}) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const userId = user!.id;
  const { notify } = useNotification();

  const [cenarios, setCenarios] = useState<Cenario[]>([]);
  const [carregandoCenarios, setCarregandoCenarios] = useState(true);
  const [cenarioId, setCenarioId] = useState("");
  const [nomes, setNomes] = useState<Record<string, string>>({});
  const [lojas, setLojas] = useState<Record<string, string>>({});

  const [pendente, setPendente] = useState<Pendente | null>(null);
  const [observacao, setObservacao] = useState("");
  const [paraEtapa, setParaEtapa] = useState<EtapaCiclo | "">("");
  const [reabrindo, setReabrindo] = useState(false);
  const [justificativa, setJustificativa] = useState("");
  const [processando, setProcessando] = useState(false);
  const paginaPartes = usePagination();
  const paginaHistorico = usePagination();
  const [ciclos, setCiclos] = useState<CicloComHistorico[]>([]);
  const [numeroCiclo, setNumeroCiclo] = useState<number | null>(null);

  const { estado, carregando, erro, recarregar } = useFluxoCenario(clientId, cenarioId || undefined);
  const cenario = cenarios.find((c) => c.id === cenarioId);

  // Só o que já tem cálculo entra em aprovação; o mais recente vem primeiro na listagem.
  useEffect(() => {
    setCarregandoCenarios(true);
    listCenarios(clientId)
      .then((todos) => {
        const aprovaveis = todos.filter((c) => c.status === "CALCULADO" || c.status === "PROMOVENDO" || c.status === "PROMOVIDO");
        setCenarios(aprovaveis);
        if (aprovaveis[0]?.id) setCenarioId(aprovaveis[0].id);
      })
      .catch(() => notify("Não foi possível carregar os cenários.", "error"))
      .finally(() => setCarregandoCenarios(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // O histórico vem de todos os ciclos do cenário, não só do aberto: depois de publicado ele continua consultável.
  // Recarrega quando o estado do ciclo muda (ação, publicação, reabertura).
  useEffect(() => {
    if (!cenarioId || !estado?.fluxoAtivo) {
      setCiclos([]);
      return;
    }
    let cancelado = false;
    cicloHistorico(cenarioId, clientId)
      .then((r) => !cancelado && setCiclos(r.ciclos))
      .catch(() => !cancelado && setCiclos([]));
    return () => {
      cancelado = true;
    };
  }, [cenarioId, clientId, estado]);

  // Outro cenário volta para o ciclo mais recente.
  useEffect(() => setNumeroCiclo(null), [cenarioId]);

  // Nomes de usuários e lojas são só rótulo: se não vierem, a tela mostra o código.
  useEffect(() => {
    fetchAllUsers()
      .then((todos) => setNomes(Object.fromEntries(todos.map((u) => [u.id, u.name]))))
      .catch(() => undefined);
    getUserCompanies(userId)
      .then((empresas) =>
        setLojas(
          Object.fromEntries(empresas.filter((c) => c.client_id === clientId && c.erp_code).map((c) => [String(c.erp_code), c.name])),
        ),
      )
      .catch(() => undefined);
  }, [clientId, userId]);

  // A publicação roda fora do request que a disparou: acompanha até o desfecho e recarrega o ciclo.
  const promovendoId = cenario?.status === "PROMOVENDO" ? cenario.id : null;
  useEffect(() => {
    if (!promovendoId) return;
    let cancelado = false;
    let timer: ReturnType<typeof setTimeout>;
    const limite = Date.now() + LIMITE_PUBLICACAO_MS;
    const consultar = async () => {
      try {
        const situacao = await statusCalculo(promovendoId, clientId);
        if (cancelado) return;
        if (situacao.status !== "PROMOVENDO") {
          setCenarios((prev) => prev.map((c) => (c.id === promovendoId ? { ...c, status: situacao.status, erroCalculo: situacao.erroCalculo } : c)));
          recarregar();
          if (situacao.erroCalculo) notify(situacao.erroCalculo, "error");
          else notify("Cenário publicado.", "success");
          return;
        }
      } catch {
        // Resposta que não chegou não é desfecho: a próxima consulta pergunta de novo.
      }
      if (!cancelado && Date.now() < limite) timer = setTimeout(consultar, INTERVALO_PUBLICACAO_MS);
    };
    timer = setTimeout(consultar, INTERVALO_PUBLICACAO_MS);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promovendoId, clientId]);

  // Outro cenário é outro ciclo: as listas voltam para a primeira página.
  useEffect(() => {
    paginaPartes.setPage(0);
    paginaHistorico.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cenarioId]);

  const nomeDe = (id: string) => nomes[id] ?? `${id.slice(0, 8)}…`;
  const lojaDe = (cod: string) => lojas[cod] ?? cod;

  const progresso = useMemo(() => progressoDoCiclo(estado?.partes ?? []), [estado]);
  const minhas = estado?.minhasAcoes ?? [];
  const podeIniciar = minhas.includes("CALCULAR") || minhas.includes("ADMINISTRAR");
  const podeReabrir = minhas.includes("REABRIR") || minhas.includes("ADMINISTRAR");
  const podePublicar = minhas.includes("PUBLICAR") || minhas.includes("ADMINISTRAR");

  const executar = async (acao: () => Promise<unknown>, sucesso: string) => {
    setProcessando(true);
    try {
      await acao();
      notify(sucesso, "success");
      recarregar();
      return true;
    } catch (err: any) {
      notify(err?.message || "Não foi possível concluir a ação.", "error");
      // O erro mais comum é outro usuário ter agido antes: recarregar mostra o que mudou.
      recarregar();
      return false;
    } finally {
      setProcessando(false);
    }
  };

  const abrirAcao = (parte: ParteCiclo, botao: BotaoCiclo) => {
    // Sem justificativa a pedir nem destino a escolher, a ação é imediata.
    if (!botao.exigeObservacao && !botao.destinos) {
      void agir(parte, botao.acao, "", "");
      return;
    }
    setObservacao("");
    setParaEtapa(botao.destinos?.[0] ?? "");
    setPendente({ parte, botao });
  };

  const agir = async (parte: ParteCiclo, acao: AcaoCiclo, texto: string, destino: EtapaCiclo | "") =>
    executar(
      () =>
        cicloAgir(
          {
            cenarioId,
            responsavelId: parte.id,
            acao,
            observacao: texto.trim() || undefined,
            versao: parte.versao,
            paraEtapa: destino || undefined,
          },
          clientId,
        ),
      "Ação registrada.",
    );

  const confirmarAcao = async () => {
    if (!pendente) return;
    if (await agir(pendente.parte, pendente.botao.acao, observacao, paraEtapa)) setPendente(null);
  };

  const iniciar = () => executar(() => cicloIniciar(cenarioId, clientId), "Ciclo de aprovação aberto.");

  const confirmarReabertura = async () => {
    if (await executar(() => cicloReabrir(cenarioId, justificativa.trim(), clientId), "Ciclo reaberto no cálculo.")) {
      setReabrindo(false);
      setJustificativa("");
    }
  };

  const publicar = async () => {
    if (!cenario) return;
    const ok = await executar(() => promoverCenario(cenario.id!, clientId), "Publicação iniciada — pode levar alguns minutos.");
    if (ok) setCenarios((prev) => prev.map((c) => (c.id === cenario.id ? { ...c, status: "PROMOVENDO" } : c)));
  };

  if (carregandoCenarios) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (cenarios.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Nenhum cenário calculado
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          A aprovação começa depois que um cenário é calculado na aba Períodos de Cálculo.
        </Typography>
      </Box>
    );
  }

  const cicloAberto = estado?.ciclo ?? null;
  const solicitaJustificativa = pendente?.botao.exigeObservacao ?? false;
  const partes = estado?.partes ?? [];
  const cicloSelecionado = ciclos.find((c) => c.numero === numeroCiclo) ?? ciclos[0];
  const historico = cicloSelecionado?.historico ?? [];
  const fatia = <T,>(lista: T[], p: { page: number; rowsPerPage: number }) =>
    lista.slice(p.page * p.rowsPerPage, p.page * p.rowsPerPage + p.rowsPerPage);

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
      >
        <Typography variant="body2" color="text.secondary">
          Acompanhe as etapas de cada responsável e publique quando todos os escopos estiverem aprovados.
        </Typography>
        {onAbrirManutencao && (
          <Button variant="outlined" onClick={onAbrirManutencao} sx={{ flexShrink: 0 }}>
            Abrir manutenção de metas
          </Button>
        )}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "center" }}>
        <TextField
          select
          size="small"
          label="Cenário"
          value={cenarioId}
          onChange={(e) => setCenarioId(e.target.value)}
          fullWidth
          sx={{ maxWidth: { sm: 460 } }}
        >
          {cenarios.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.descricao} — {situacaoDe(statusCalculoDe(c.status ?? "RASCUNHO"), true).toLowerCase()}
            </MenuItem>
          ))}
        </TextField>
        {cenario && (
          <Stack gap={0.5}>
            <Chip
              size="small"
              variant="outlined"
              label={situacaoDe(statusCalculoDe(cenario.status ?? "RASCUNHO"), true)}
              color={corSituacao(statusCalculoDe(cenario.status ?? "RASCUNHO"))}
              sx={{ alignSelf: "flex-start" }}
            />
            <Typography variant="caption" color="text.secondary">
              Meta de {rotuloMes(cenario.anoMeta, cenario.mesMeta)}.
            </Typography>
          </Stack>
        )}
      </Stack>

      {carregando && !estado ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : erro === "transitorio" && !estado ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={recarregar}>
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar o ciclo de aprovação (falha de comunicação).
        </Alert>
      ) : erro === "negado" || !estado ? (
        <Alert severity="warning">Você não é responsável por este cenário ou não foi possível carregar o ciclo de aprovação.</Alert>
      ) : !estado.fluxoAtivo ? (
        <Alert severity="info">
          O fluxo de aprovação não está ativo: nenhum responsável foi cadastrado. Cadastre-os na aba Permissões para passar a exigir aprovação antes de publicar.
        </Alert>
      ) : !cicloAberto ? (
        <Box sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Stack gap={2} alignItems="flex-start">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {estado.ultimoCiclo ? `Ciclo ${estado.ultimoCiclo.numero} publicado` : "Nenhum ciclo de aprovação aberto"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cenario?.status === "PROMOVIDO"
                ? "As metas publicadas seguem vigentes. Reabrir o ciclo recomeça a aprovação no cálculo e só substitui as metas quando a nova publicação terminar."
                : "Abrir o ciclo copia os responsáveis cadastrados hoje e coloca o escopo de cada um na primeira etapa dele."}
            </Typography>
            {cenario?.status === "PROMOVIDO" ? (
              <Button variant="contained" disabled={!podeReabrir || processando} onClick={() => setReabrindo(true)}>
                Reabrir ciclo
              </Button>
            ) : (
              <Button variant="contained" disabled={!podeIniciar || processando || cenario?.status !== "CALCULADO"} onClick={iniciar}>
                Iniciar ciclo de aprovação
              </Button>
            )}
            {!(cenario?.status === "PROMOVIDO" ? podeReabrir : podeIniciar) && (
              <Typography variant="caption" color="text.secondary">
                Você não tem a ação necessária para isso.
              </Typography>
            )}
          </Stack>
        </Box>
      ) : (
        <>
          {/* Mesmo realce de "pronto / pendente" do total de pesos na aba Períodos de Cálculo. */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={2}
            justifyContent="space-between"
            aria-live="polite"
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: estado.prontoParaPublicar ? "success.main" : "warning.main",
              bgcolor: (theme) => alpha(estado.prontoParaPublicar ? theme.palette.success.main : theme.palette.warning.main, 0.08),
            }}
          >
            <Stack gap={1}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Ciclo {cicloAberto.numero}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={estado.prontoParaPublicar ? "success" : "warning"}
                  label={estado.prontoParaPublicar ? "Pronto para publicar" : "Publicação bloqueada"}
                />
                <Chip size="small" variant="outlined" label={`${progresso.concluidos} de ${progresso.total} escopos concluídos`} />
              </Stack>
              {cicloAberto.justificativa && (
                <Typography variant="body2" color="text.secondary">
                  Reaberto: {cicloAberto.justificativa}
                </Typography>
              )}
              {estado.bloqueios.length > 0 && (
                <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {estado.bloqueios.map((b) => (
                    <Typography key={b} component="li" variant="body2" color="text.secondary">
                      {b}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
            <Stack direction="row" gap={1} alignItems="flex-start" flexShrink={0}>
              <Button variant="outlined" disabled={!podeReabrir || processando} onClick={() => setReabrindo(true)}>
                Reabrir no cálculo
              </Button>
              <Tooltip title={estado.prontoParaPublicar ? "" : "Há pendências para publicar"}>
                <span>
                  <Button
                    variant="contained"
                    disabled={!podePublicar || !estado.prontoParaPublicar || processando || cenario?.status === "PROMOVENDO"}
                    startIcon={cenario?.status === "PROMOVENDO" ? <CircularProgress size={14} /> : undefined}
                    onClick={publicar}
                  >
                    {cenario?.status === "PROMOVENDO" ? "Publicando..." : cenario?.status === "PROMOVIDO" ? "Republicar" : "Publicar"}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <DataTable
            data={fatia(partes, paginaPartes)}
            columns={[
              {
                key: "responsavel",
                label: "Responsável",
                render: (parte) => (
                  <>
                    {nomeDe(parte.userId)}
                    {parte.userId === userId && <Chip size="small" label="você" sx={{ ml: 1 }} />}
                  </>
                ),
              },
              // Cada ação tem o próprio escopo: a coluna mostra o da etapa em que a parte está.
              {
                key: "lojas",
                label: "Lojas",
                render: (parte) => {
                  const { lojas: daEtapa } = escopoDaEtapa(parte);
                  return daEtapa.length === 0 ? (
                    "—"
                  ) : (
                    <Tooltip title={daEtapa.map(lojaDe).join(", ")}>
                      <span>{`${daEtapa.length} loja(s)`}</span>
                    </Tooltip>
                  );
                },
              },
              {
                key: "categorias",
                label: "Categorias",
                render: (parte) => {
                  const { categorias } = escopoDaEtapa(parte);
                  return categorias.length === 0 ? "—" : `${categorias.length} categoria(s)`;
                },
              },
              {
                key: "acoes",
                label: "Ações no fluxo",
                render: (parte) => (
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {parte.acoes.map((a) => (
                      <Chip key={a} size="small" variant="outlined" label={ACAO_ROTULO[a]} />
                    ))}
                  </Stack>
                ),
              },
              {
                key: "etapa",
                label: "Etapa",
                render: (parte) => (
                  <Chip size="small" variant="outlined" color={ETAPA_COR[parte.etapa]} label={ETAPA_ROTULO[parte.etapa]} />
                ),
              },
            ]}
            total={partes.length}
            page={paginaPartes.page}
            rowsPerPage={paginaPartes.rowsPerPage}
            onPageChange={paginaPartes.setPage}
            onRowsPerPageChange={paginaPartes.setRowsPerPage}
            actions={(parte) => (
              <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
                {botoesDaParte(estado, parte, userId).map((b) => (
                  <Button
                    key={b.acao}
                    size="small"
                    variant={b.acao === "DEVOLVER" || b.acao === "DIRECIONAR" ? "outlined" : "contained"}
                    color={b.acao === "DEVOLVER" ? "warning" : "primary"}
                    disabled={processando}
                    onClick={() => abrirAcao(parte, b)}
                  >
                    {b.rotulo}
                  </Button>
                ))}
              </Stack>
            )}
          />
        </>
      )}

      {estado?.fluxoAtivo && erro !== "negado" && (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "center" }} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Histórico do ciclo
            </Typography>
            {ciclos.length > 0 && (
              <TextField
                select
                size="small"
                label="Ciclo"
                value={cicloSelecionado?.numero ?? ""}
                onChange={(e) => {
                  setNumeroCiclo(Number(e.target.value));
                  paginaHistorico.setPage(0);
                }}
                sx={{ minWidth: 260 }}
              >
                {ciclos.map((c) => (
                  <MenuItem key={c.id} value={c.numero}>
                    {`Ciclo ${c.numero} — ${c.status === "ABERTO" ? "em andamento" : `publicado em ${dataHora(c.publicadoEm ?? c.criadoEm)}`}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
          {cicloSelecionado?.justificativa && (
            <Typography variant="body2" color="text.secondary">
              Reaberto: {cicloSelecionado.justificativa}
            </Typography>
          )}
          {historico.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              Nenhuma manifestação registrada ainda.
            </Typography>
          ) : (
            <DataTable
              data={fatia(historico, paginaHistorico)}
              columns={[
                { key: "data", label: "Data", render: (h) => dataHora(h.data) },
                { key: "escopo", label: "Escopo de", render: (h) => nomeDe(h.responsavelUserId) },
                { key: "acao", label: "Ação", render: (h) => MANIFESTACAO_ROTULO[h.acao] ?? h.acao },
                {
                  key: "etapa",
                  label: "Etapa",
                  render: (h) => `${ETAPA_ROTULO[h.etapaAnterior]} → ${ETAPA_ROTULO[h.etapaNova]}`,
                },
                {
                  key: "por",
                  label: "Por",
                  render: (h) => (h.automatica ? <Chip size="small" variant="outlined" label="automático" /> : nomeDe(h.autorId)),
                },
                {
                  key: "observacao",
                  label: "Observação",
                  // A célula da tabela não quebra linha; a justificativa pode ser longa.
                  render: (h) => <Box sx={{ whiteSpace: "normal", minWidth: 200 }}>{h.observacao || "—"}</Box>,
                },
              ]}
              total={historico.length}
              page={paginaHistorico.page}
              rowsPerPage={paginaHistorico.rowsPerPage}
              onPageChange={paginaHistorico.setPage}
              onRowsPerPageChange={paginaHistorico.setRowsPerPage}
            />
          )}
        </>
      )}

      <Dialog open={!!pendente} onClose={() => !processando && setPendente(null)} fullWidth maxWidth="sm">
        <DialogTitle>{pendente?.botao.rotulo}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            {pendente?.botao.destinos && (
              <TextField select size="small" label="Direcionar para" value={paraEtapa} onChange={(e) => setParaEtapa(e.target.value as EtapaCiclo)}>
                {pendente.botao.destinos.map((e) => (
                  <MenuItem key={e} value={e}>
                    {ETAPA_ROTULO[e]}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label={solicitaJustificativa ? "Justificativa" : "Observação"}
              required={solicitaJustificativa}
              multiline
              minRows={3}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              helperText={`${observacao.length}/500`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setPendente(null)} disabled={processando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmarAcao}
            disabled={processando || (solicitaJustificativa && !observacao.trim()) || (!!pendente?.botao.destinos && !paraEtapa)}
            startIcon={processando ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reabrindo} onClose={() => !processando && setReabrindo(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reabrir no cálculo</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              {cicloAberto
                ? "Todos os escopos voltam à primeira etapa e as aprovações dadas até aqui deixam de valer."
                : "Abre um novo ciclo com o cadastro de responsáveis de hoje. O ciclo anterior fica no histórico."}
            </Typography>
            <TextField
              label="Justificativa"
              required
              multiline
              minRows={3}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              helperText={`${justificativa.length}/500`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setReabrindo(false)} disabled={processando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmarReabertura}
            disabled={processando || !justificativa.trim()}
            startIcon={processando ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Reabrir
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
